import { Operation } from '@electric-sql/client';
import { createReducer } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { Wallet } from 'dragonmouse-shared/economy';
import {
	Chat,
	DropShadowMode,
	Frame,
	Scene,
	Subscription,
	Tier,
	User,
} from 'dragonmouse-shared/interfaces';
import { Style } from 'dragonmouse-shared/style';
import { computeWalletLevels, WalletLevels } from 'economy';
import {
	LoadingState,
	optimisticallyUpdateChats,
	optimisticallyUpdateFrames,
	optimisticallyUpdateScenes,
	optimisticallyUpdateUser,
	refreshFramePermissions,
	resolveOptimisticallyUpdatedChats,
	resolveOptimisticallyUpdatedFrames,
	resolveOptimisticallyUpdatedScenes,
	resolveOptimisticallyUpdatedUser,
	setLoadingState,
	setSceneLoadError,
	updateChats,
	updateFrames,
	updateGuestFrames,
	updateGuestScenes,
	updateScenes,
	updateSubscription,
	updateTrackedUsers,
	updateUser,
	updateWallet,
} from './actions';
import { optimisticWriteIsResolvedByIds } from './util';

export type OptimisticWrite<T> = {
	id: string;
	operation: Operation;
	value: Partial<T> & { id: string };
};

// definition of our store's types
export type Store = {
	scenes: Scene[];
	guestScenes: Scene[];
	frames: Frame[];
	guestFrames: Frame[];
	chats: Chat[];
	user: User | null;
	trackedUsers: User[];
	subscription: Subscription | null;
	wallet: Wallet | null;
	optimisticSceneWrites: OptimisticWrite<Scene>[];
	optimisticFrameWrites: OptimisticWrite<Frame>[];
	optimisticChatWrites: OptimisticWrite<Chat>[];
	optimisticUserWrites: OptimisticWrite<User>[];
	loading: LoadingState;
	sceneLoadError: string | null;
};

// initialize our slice of the store to its default value
const initialState: Store = {
	scenes: [],
	guestScenes: [],
	frames: [],
	guestFrames: [],
	chats: [],
	user: null,
	trackedUsers: [],
	subscription: null,
	wallet: null,
	optimisticSceneWrites: [],
	optimisticFrameWrites: [],
	optimisticChatWrites: [],
	optimisticUserWrites: [],
	loading: {
		user: 'never-loaded',
		sceneIds: 'never-loaded',
		scenes: 'never-loaded',
		activeScene: {
			frames: 'never-loaded',
			chats: 'never-loaded',
		},
		guest: {
			scenes: 'never-loaded',
			frames: 'never-loaded',
		},
	},
	sceneLoadError: null,
};

export const DEFAULT_CANVAS_COLOR: string = '#555555';
export const DEFAULT_CANVAS_DROP_SHADOW_MODE: DropShadowMode = DropShadowMode.MEDIUM;

function mergeOptimisticWrites<T>(
	existingWrites: OptimisticWrite<T>[],
	newWrites: OptimisticWrite<T>[]
): OptimisticWrite<T>[] {
	const newIds = newWrites.map((w) => w.id);
	return [
		// First we keep any optimistic writes that are not being added or updated.
		...existingWrites.filter((w) => !newIds.includes(w.id)),
		// Then we add the new optimistic writes. This moves both new and updated
		// writes to the end of the array which is what we want.
		...newWrites,
	];
}

const reducer = createReducer(initialState, (builder) => {
	//------------------------------------------------------------------------------------------------
	// scenes
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateScenes, (state, action) => {
		state.scenes = action.payload;
	});

	builder.addCase(updateGuestScenes, (state, action) => {
		state.guestScenes = action.payload;
	});

	builder.addCase(optimisticallyUpdateScenes, (state, action) => {
		state.optimisticSceneWrites = mergeOptimisticWrites(
			state.optimisticSceneWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedScenes, (state, action) => {
		state.optimisticSceneWrites = state.optimisticSceneWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	builder.addCase(setSceneLoadError, (state, action) => {
		state.sceneLoadError = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// frames
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateFrames, (state, action) => {
		state.frames = action.payload;
	});

	builder.addCase(refreshFramePermissions, (state) => {
		// Robustly update frame permissions based on current scenes collection
		// If a frame's scene is in the user's scenes collection, it's editable (viewOnly: false)
		// If a frame's scene is not in the collection, it's view-only (viewOnly: true)
		const userSceneIds = new Set(state.scenes.map((scene) => scene.id));
		state.frames = state.frames.map((frame) => ({
			...frame,
			viewOnly: !userSceneIds.has(frame.sceneId),
		}));
	});

	builder.addCase(updateGuestFrames, (state, action) => {
		state.guestFrames = action.payload;
	});

	builder.addCase(optimisticallyUpdateFrames, (state, action) => {
		const newIds = action.payload.map((w) => w.id);
		state.optimisticFrameWrites = [
			// First we keep any optimistic writes that are not being added or updated.
			...state.optimisticFrameWrites.filter((w) => !newIds.includes(w.id)),
			// Then we add the new optimistic writes. This moves both new and updated
			// writes to the end of the array which is what we want.
			...action.payload,
		];
	});

	builder.addCase(resolveOptimisticallyUpdatedFrames, (state, action) => {
		state.optimisticFrameWrites = state.optimisticFrameWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	//------------------------------------------------------------------------------------------------
	// chats
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateChats, (state, action) => {
		// Detect if existing chats disappeared
		const newChatIds = new Set(action.payload.map((c) => c.id));
		const disappearedChats = state.chats.filter((c) => !newChatIds.has(c.id));

		if (disappearedChats.length > 0 && state.chats.length > 0) {
			const message = `[REDUX] ${disappearedChats.length} chats disappeared in updateChats`;
			console.warn(
				message,
				disappearedChats.map((c) => ({ id: c.id, title: c.title }))
			);
			Sentry.captureMessage(message);
		}

		state.chats = action.payload;
	});

	builder.addCase(optimisticallyUpdateChats, (state, action) => {
		const newIds = action.payload.map((w) => w.id);
		state.optimisticChatWrites = [
			// First we keep any optimistic writes that are not being added or updated.
			...state.optimisticChatWrites.filter((w) => !newIds.includes(w.id)),
			// Then we add the new optimistic writes. This moves both new and updated
			// writes to the end of the array which is what we want.
			...action.payload,
		];
	});

	builder.addCase(resolveOptimisticallyUpdatedChats, (state, action) => {
		state.optimisticChatWrites = state.optimisticChatWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	//------------------------------------------------------------------------------------------------
	// user
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateUser, (state, action) => {
		state.user = action.payload;
	});

	builder.addCase(optimisticallyUpdateUser, (state, action) => {
		state.optimisticUserWrites = mergeOptimisticWrites(state.optimisticUserWrites, action.payload);
	});

	builder.addCase(resolveOptimisticallyUpdatedUser, (state, action) => {
		state.optimisticUserWrites = state.optimisticUserWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	//------------------------------------------------------------------------------------------------
	// subscription
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateSubscription, (state, action) => {
		state.subscription = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// wallet
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateWallet, (state, action) => {
		state.wallet = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// tracked users
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateTrackedUsers, (state, action) => {
		state.trackedUsers = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// loading state
	//------------------------------------------------------------------------------------------------

	builder.addCase(setLoadingState, (state, action) => {
		const { resource, state: newState } = action.payload;

		// Handle nested paths like 'activeScene.frames'
		if (resource.includes('.')) {
			const [parent, child] = resource.split('.');
			if (parent === 'activeScene') {
				(state.loading.activeScene as any)[child] = newState;
			} else if (parent === 'guest') {
				(state.loading.guest as any)[child] = newState;
			}
		} else {
			// Handle top-level resources
			if (resource === 'user') {
				state.loading.user = newState;
			} else if (resource === 'sceneIds') {
				state.loading.sceneIds = newState;
			} else if (resource === 'scenes') {
				state.loading.scenes = newState;
			}
		}
	});
});

//------------------------------------------------------------------------------------------------
// selectors
//------------------------------------------------------------------------------------------------
export const selectActiveScene = (state: Store, activeSceneId: string | null): Scene | null => {
	if (!activeSceneId) return null;

	// Check regular scenes first (they take precedence over guest scenes)
	const scene = state.scenes.find((scene) => scene.id === activeSceneId) || null;
	if (scene) {
		// Apply optimistic writes to regular scenes
		const optimisticWrites = Object.values(state.optimisticSceneWrites);
		return optimisticWrites
			.filter((w) => w.value.id === activeSceneId)
			.reduce((scene: Scene | null, { operation, value }: OptimisticWrite<Scene>): Scene | null => {
				switch (operation) {
					case 'insert':
						return value as Scene;
					case 'update':
						return { ...scene, ...value } as Scene;
					case 'delete':
						return null;
					default:
						return scene;
				}
			}, scene);
	}

	// Fall back to guest scenes if not found in regular scenes
	// guest scenes are readonly so we don't need to check optimistic writes
	const guestScene = state.guestScenes.find((scene) => scene.id === activeSceneId) || null;
	return guestScene;
};

export const selectActiveStyle = (state: Store, activeSceneId: string | null): Style | null => {
	const scene = selectActiveScene(state, activeSceneId);
	if (!scene?.metadata?.styles?.length) return null;
	const currentStyle = scene.metadata?.currentStyle;
	if (currentStyle === null) return null;
	if (currentStyle >= scene.metadata.styles.length) return null;
	return scene.metadata.styles[currentStyle];
};

export const selectActiveChat = (state: Store, activeChatId: string | null): Chat | null => {
	if (!activeChatId) return null;

	const chat = state.chats.find((chat) => chat.id === activeChatId) || null;
	const optimisticWrites = Object.values(state.optimisticChatWrites);

	return optimisticWrites
		.filter((w) => w.value.id === activeChatId)
		.reduce((chat: Chat | null, { operation, value }: OptimisticWrite<Chat>): Chat | null => {
			switch (operation) {
				case 'insert':
					return value as Chat;
				case 'update':
					return { ...chat, ...value } as Chat;
				case 'delete':
					return null;
				default:
					return chat;
			}
		}, chat);
};

export const selectScenes = (state: Store): Scene[] => {
	const optimisticWrites = Object.values(state.optimisticSceneWrites);

	const scenes = optimisticWrites.reduce(
		(scenes: Scene[], { operation, value }: OptimisticWrite<Scene>): Scene[] => {
			switch (operation) {
				case 'insert':
					return [...scenes, value as Scene];
				case 'update':
					return scenes.map((scene) => (scene.id === value.id ? { ...scene, ...value } : scene));
				case 'delete':
					return scenes.filter((scene) => scene.id !== value.id);
				default:
					return scenes;
			}
		},
		state.scenes
	);

	// Filter out guest scenes that already exist in regular scenes to avoid duplicates
	const regularSceneIds = new Set(scenes.map((scene) => scene.id));
	const uniqueGuestScenes = state.guestScenes.filter(
		(guestScene) => !regularSceneIds.has(guestScene.id)
	);

	return [...scenes, ...uniqueGuestScenes];
};

export const selectGuestScenes = (state: Store): Scene[] => {
	const optimisticWrites = Object.values(state.optimisticSceneWrites);

	const scenes = optimisticWrites.reduce(
		(scenes: Scene[], { operation, value }: OptimisticWrite<Scene>): Scene[] => {
			switch (operation) {
				case 'insert':
					return [...scenes, value as Scene];
				case 'update':
					return scenes.map((scene) => (scene.id === value.id ? { ...scene, ...value } : scene));
				case 'delete':
					return scenes.filter((scene) => scene.id !== value.id);
				default:
					return scenes;
			}
		},
		state.scenes
	);

	// Filter out guest scenes that already exist in regular scenes to avoid duplicates
	const regularSceneIds = new Set(scenes.map((scene) => scene.id));
	const uniqueGuestScenes = state.guestScenes.filter(
		(guestScene) => !regularSceneIds.has(guestScene.id)
	);

	return uniqueGuestScenes;
};

export const selectFrames = (state: Store, includeDeleted: boolean = false): Frame[] => {
	const optimisticWrites = Object.values(state.optimisticFrameWrites);
	const frames = optimisticWrites.reduce(
		(frames: Frame[], { operation, value }: OptimisticWrite<Frame>): Frame[] => {
			switch (operation) {
				case 'insert':
					return [...frames, value as Frame];
				case 'update':
					return frames.map((frame) => (frame.id === value.id ? { ...frame, ...value } : frame));
				case 'delete':
					return frames.filter((frame) => includeDeleted || frame.id !== value.id);
				default:
					return frames;
			}
		},
		state.frames
	);

	if (!includeDeleted) {
		return frames.filter((frame) => !frame.deletedAt);
	}

	// add the guest frames to the list
	return [...frames, ...state.guestFrames];
};

export const selectFrame = (state: Store, frameId?: string): Frame | null => {
	if (!frameId) return null;

	const frame = state.frames.find((frame) => frame.id === frameId) ?? null;

	// XXX
	// XXX
	//
	// I've identified a conceptual bug with optimistic writes. I'm opting *not* to fix it now because:
	//  - we haven't noticed any issues
	// - I believe it's only when doing local development that this issue manifests.
	// - The solution I see involves a sizable amount of code
	// - I don't want to slow down to build the better solution now.
	// - I don't want to risk introducing an instability for no good reason
	//
	// However let me document what is going on
	// The issue is when state.optimisticFrameWrites contains multiple writes for the same frame.
	// THis was causing a bug when toggling the public/private toggle on frame sharing. On local dev,
	// these changes would stack up, and only the first change would be applied.
	// You can see below I updated the code here to use the last write, which fixes this particular bug.
	//
	// Two things concern me:
	//  - this is not a solid fix for the situation where the multiple writes affect different properties of
	//    the frame. they are not merged!
	//  - other methods here like selectChats or selectFrames are not updated to use the last write.
	//    and they also don't merge the writes!
	//
	// I envision three possible solution directions
	//
	// PATH A
	// - make a function to merge updates from frames, chats, scenes, etc.
	// - here use that function to merge multiple writes for the same frame
	// - in the other methods, merge multiple writes for the same frame/chat/scene/etc and THEN do the reduce
	//
	// PATH B
	// - as optimistic writes get set, merge them when there's already a pending optimistic write for the same frame
	//
	// PATH C
	// - do something completely different.
	//
	// XXX
	// XXX

	// If there are multiple writes for this frame, use the last one (most recent)
	const optimisticWrites = Object.values(state.optimisticFrameWrites);
	const writes = optimisticWrites.filter((write) => write.value.id === frameId);
	if (writes.length > 0) {
		const write = writes[writes.length - 1];
		if (write.value.id === frameId) {
			switch (write.operation) {
				case 'insert':
					return write.value as Frame;
				case 'update':
					return { ...frame, ...write.value } as Frame;
				case 'delete':
					return null;
				default:
					return frame;
			}
		}
	}

	// If we found a frame in regular frames, return it
	if (frame) {
		return frame;
	}

	// If we didn't find it in the regular frames, check the guest frames
	const guestFrame = state.guestFrames.find((guestFrame) => guestFrame.id === frameId);
	if (guestFrame) {
		return guestFrame;
	}

	return null;
};

export const selectChats = (state: Store): Chat[] => {
	const optimisticWrites = Object.values(state.optimisticChatWrites);
	return optimisticWrites.reduce(
		(chats: Chat[], { operation, value }: OptimisticWrite<Chat>): Chat[] => {
			switch (operation) {
				case 'insert':
					return [...chats, value as Chat];
				case 'update':
					return chats.map((chat) => (chat.id === value.id ? { ...chat, ...value } : chat));
				case 'delete':
					return chats.filter((chat) => chat.id !== value.id);
				default:
					return chats;
			}
		},
		state.chats
	);
};

export const selectUser = (state: Store): User | null => {
	const optimisticWrites = Object.values(state.optimisticUserWrites);

	if (optimisticWrites.length === 0) {
		return state.user;
	}

	return optimisticWrites.reduce(
		(user: User | null, { operation, value }: OptimisticWrite<User>): User | null => {
			switch (operation) {
				case 'insert':
					return value as User;
				case 'update':
					// If user is null, we can't update it, so return null
					// If user exists, merge the partial update
					return user ? ({ ...user, ...value } as User) : null;
				case 'delete':
					return null;
				default:
					return user;
			}
		},
		state.user ?? null
	);
};

export const selectTrackedUser = (state: Store, userId: string): User | null => {
	return state.trackedUsers.find((user) => user.id === userId) || null;
};

export const selectWallet = (state: Store): Wallet | null => {
	return state.wallet;
};

export const selectWalletLevels = (state: Store): WalletLevels | null => {
	const { wallet, subscription } = state;
	// log a sentry error specifically for this case
	// every user should have a wallet, not having one gives infinite gold
	// if the user has loaded and still doesn't have a wallet, log an error
	if (!wallet && state.loading.user === 'loaded') {
		Sentry.captureException('No wallet found in selectWalletLevels', {
			extra: {
				subscription: subscription,
			},
		});
	}
	if (!wallet) return null;

	// free users have a wallet, but no subscription
	return computeWalletLevels(wallet, subscription?.tier ?? Tier.FREE);
};

export const selectIsFreeTier = (state: Store): boolean => {
	return (state.subscription?.tier ?? Tier.FREE) === Tier.FREE;
};

export default reducer;
